import React, { useState, useRef, useCallback } from "react";
import { Stack, Text, Box } from "@chakra-ui/react";
import { YAxis, ResponsiveContainer, Bar, BarChart, Cell, XAxis, LabelList, ReferenceLine } from "recharts";
import { CustomTooltip } from "./CustomChartUtils";
import type { DataType } from "hooks/useStats";
import { motion } from "framer-motion";

export const getScoreColor = (value: number): string => {
  const normalizedValue = value >= 0 && value <= 10 ? value : 0;

  if (normalizedValue === 0) {
    return "#A0AEC0";
  } else if (normalizedValue <= 3) {
    return "#F7944E";
  } else if (normalizedValue <= 6) {
    return "#FFCD54";
  } else {
    return "#78ACE3";
  }
};

interface Props {
  data: DataType[];
  width?: number | string;
  title?: string;
  onClick?: (event: any) => void;
  chartId?: string;
  showStateTitle?: boolean;
  barColor?: string;
  yAxisType?: "number" | "category";
  height?: string;
  chartHeight?: number | string;
  showTooltip?: boolean;
  blurContent?: boolean;
}

interface TooltipDataState {
  active: boolean;
  payload?: Array<{
    value: number;
    payload: DataType;
    dataKey?: string;
  }>;
  position?: { x: number; y: number };
  index?: number;
}

const MotionBox = motion(Box);

export const CustomVerticalBarChart: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({
  data,
  width = "100%",
  title,
  onClick,
  chartId,
  showStateTitle = false,
  barColor,
  yAxisType = "number",
  height = "18rem",
  chartHeight = 100,
  showTooltip = true,
  blurContent,
}) => {
  const [tooltipData, setTooltipData] = useState<TooltipDataState>({ active: false });
  const chartRef = useRef<HTMLDivElement>(null);

  const handleMouseOver = useCallback((data: any) => {
    if (data && data.activePayload && data.activePayload.length) {
      setTooltipData({
        active: true,
        payload: data.activePayload,
        position: {
          x: data.activeCoordinate?.x ?? 0,
          y: data.activeCoordinate?.y ?? 0,
        },
        index: data.activeTooltipIndex,
      });
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTooltipData({ active: false });
  }, []);

  const handleMouseMoveRect = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      const chartElement = (e.target as SVGElement).ownerSVGElement;
      if (!chartElement) return;
      const rect = chartElement.getBoundingClientRect();
      const y = e.clientY - rect.top;
      const dataLength = data.length;
      if (dataLength === 0) return;

      const chartPadding = { top: 10, bottom: 0, left: 100 };
      const effectiveHeight = rect.height - chartPadding.top - chartPadding.bottom;

      const normalizedY = (y - chartPadding.top) / effectiveHeight;
      if (normalizedY < 0 || normalizedY > 1) return;

      const index = Math.min(Math.max(0, Math.floor(normalizedY * dataLength)), dataLength - 1);

      if (index >= 0 && index < dataLength) {
        const item = data[index];
        const yCoord = chartPadding.top + ((index + 0.5) / dataLength) * effectiveHeight;

        const chartEvent = {
          activeCoordinate: { x: rect.width / 2, y: yCoord },
          activePayload: [
            {
              value: item.value,
              payload: item,
              dataKey: "value",
            },
          ],
          activeTooltipIndex: index,
        };
        handleMouseOver(chartEvent);
      }
    },
    [data, handleMouseOver]
  );

  const tooltipY = tooltipData.position?.y ?? 0;
  const chartContainerWidth = chartRef.current?.clientWidth ?? 0;
  const tooltipWidth = 100;
  const tooltipOffset = -20;
  const maxDataValue = 10;

  let tooltipLeft = 0;
  let tooltipTop = 0;

  tooltipTop = tooltipY + tooltipOffset;
  tooltipLeft = chartContainerWidth - tooltipWidth - 20;

  return (
    <Stack height={height} width={width} p="2px" position="relative" ref={chartRef}>
      {showTooltip && tooltipData.active && tooltipData.payload && tooltipData.payload.length > 0 && (
        <MotionBox
          position="absolute"
          style={{
            ...(typeof tooltipTop === "string" ? { top: tooltipTop, y: "-50%" } : { top: tooltipTop }),
          }}
          initial={{
            x: tooltipLeft,
          }}
          animate={{
            x: tooltipLeft,
          }}
          transition={{
            type: "spring",
            stiffness: 500,
            damping: 30,
          }}
          zIndex={1000}
          width={`${tooltipWidth}px`}>
          <CustomTooltip
            active={tooltipData.active}
            payload={tooltipData.payload.map((item) => ({ payload: item.payload }))}
            color="white"
            showState={showStateTitle}
          />
        </MotionBox>
      )}
      <ResponsiveContainer width={"100%"} height={chartHeight}>
        <BarChart
          key={chartId}
          layout="vertical"
          id={chartId}
          data={data}
          margin={{ top: 0, right: 14, left: 0, bottom: 10 }}
          onMouseMove={handleMouseOver}
          onMouseLeave={handleMouseLeave}>
          <YAxis type={yAxisType} dataKey="label" axisLine={false} fontSize="13px" width={120} />
          <XAxis
            domain={[0, maxDataValue]}
            type="number"
            fontSize="10px"
            dataKey="value"
            height={10}
            ticks={Array.from({ length: maxDataValue + 1 }, (_, i) => i)}
          />
          {Array.from({ length: maxDataValue }, (_, i) => i + 1).map((value) => (
            <ReferenceLine key={`ref-line-${value}`} x={value} stroke="#E2E8F0" strokeDasharray="3 3" />
          ))}
          <Bar maxBarSize={15} minPointSize={10} onClick={(data) => onClick?.(data)} dataKey="value">
            {!showTooltip && <LabelList dataKey="value" position="right" fontSize="12px" fontWeight={"bold"} />}
            {data.map((entry, index) => (
              <Cell
                onClick={(data) => onClick?.(data)}
                key={`cell-${index}`}
                cursor="pointer"
                fill={entry.color ? entry.color : barColor ? barColor : "#4A5568"}
              />
            ))}
          </Bar>
          <rect
            x={0}
            y={0}
            width="100%"
            height="100%"
            fill="transparent"
            onMouseMove={handleMouseMoveRect}
            style={{ pointerEvents: "all", zIndex: 5 }}
          />
        </BarChart>
      </ResponsiveContainer>
      {title && (
        <Text mt="0!important" fontSize={"xs"} width={"100%"} align={"center"} pl="3rem">
          {title}
        </Text>
      )}
    </Stack>
  );
};
